import React, { useState, useEffect } from 'react'
import { 
  Table, 
  Button, 
  Space, 
  Tag, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Upload,
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  InputNumber,
  Divider
} from 'antd'
import { 
  PlusOutlined, 
  UploadOutlined, 
  EditOutlined, 
  DeleteOutlined,
  DownloadOutlined,
  SearchOutlined
} from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { API_BASE_URL, apiRequest } from '../../utils/api'

const { Option } = Select

function BookManagement() {
  const navigate = useNavigate()
  const [books, setBooks] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingBook, setEditingBook] = useState(null)
  const [form] = Form.useForm()
  const [categories, setCategories] = useState([])
  const [dynamicFields, setDynamicFields] = useState([])
  
  // 获取书籍列表
  useEffect(() => {
    fetchBooks()
    fetchCategories()
    fetchDynamicFields()
  }, [])
  
  const fetchBooks = async () => {
    setLoading(true)
    try {
      const response = await apiRequest('/admin/books')
      const data = await response.json()
      
      if (response.ok) {
        setBooks(data.books || [])
      }
    } catch (error) {
      console.error('获取书籍列表失败:', error)
      message.error('获取书籍列表失败')
    } finally {
      setLoading(false)
    }
  }
  
  const fetchCategories = async () => {
    try {
      const response = await apiRequest('/categories')
      const data = await response.json()
      
      if (response.ok) {
        setCategories(data.categories || [])
      }
    } catch (error) {
      console.error('获取分类失败:', error)
    }
  }
  
  const fetchDynamicFields = async () => {
    try {
      const response = await apiRequest('/admin/fields')
      const data = await response.json()
      
      if (response.ok) {
        setDynamicFields(data.fields || [])
      }
    } catch (error) {
      console.error('获取动态字段失败:', error)
    }
  }
  
  const handleAddBook = () => {
    setEditingBook(null)
    form.resetFields()
    setModalVisible(true)
  }
  
  const handleEditBook = (book) => {
    setEditingBook(book)
    form.setFieldsValue({
      title: book.title,
      author: book.author,
      dynasty: book.dynasty,
      category_id: book.category_id,
      ...book.dynamicFields
    })
    setModalVisible(true)
  }
  
  const handleDeleteBook = async (bookId) => {
    try {
      const response = await apiRequest(`/admin/books/${bookId}`, {
        method: 'DELETE'
      })
      
      if (response.ok) {
        message.success('书籍删除成功')
        fetchBooks()
      } else {
        message.error('书籍删除失败')
      }
    } catch (error) {
      console.error('删除书籍失败:', error)
      message.error('删除书籍失败')
    }
  }
  
  const handleModalOk = async () => {
    try {
      const values = await form.validateFields()
      
      const method = editingBook ? 'PUT' : 'POST'
      const url = editingBook 
        ? `/api/admin/books/${editingBook.id}` 
        : '/api/admin/books'
      
      const response = await apiRequest(url, {
        method,
        body: JSON.stringify(values)
      })
      
      if (response.ok) {
        message.success(editingBook ? '书籍更新成功' : '书籍添加成功')
        setModalVisible(false)
        form.resetFields()
        fetchBooks()
      } else {
        message.error(editingBook ? '书籍更新失败' : '书籍添加失败')
      }
    } catch (error) {
      console.error('保存书籍失败:', error)
      message.error('保存书籍失败')
    }
  }
  
  const handleUpload = async (file) => {
    // 这里实现文件上传逻辑
    message.info('文件上传功能开发中...')
    return false
  }
  
  const columns = [
    {
      title: '书名',
      dataIndex: 'title',
      key: 'title',
      sorter: (a, b) => a.title.localeCompare(b.title),
    },
    {
      title: '作者',
      dataIndex: 'author',
      key: 'author',
    },
    {
      title: '朝代',
      dataIndex: 'dynasty',
      key: 'dynasty',
    },
    {
      title: '分类',
      dataIndex: 'category_name',
      key: 'category_name',
    },
    {
      title: '页数',
      dataIndex: 'page_count',
      key: 'page_count',
      sorter: (a, b) => a.page_count - b.page_count,
    },
    {
      title: '大小',
      dataIndex: 'file_size',
      key: 'file_size',
      render: (size) => `${(size / 1024 / 1024).toFixed(1)} MB`,
      sorter: (a, b) => a.file_size - b.file_size,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button 
            type="primary" 
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditBook(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除这本书吗？"
            onConfirm={() => handleDeleteBook(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              type="primary" 
              danger 
              icon={<DeleteOutlined />}
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]
  
  // 构建分类树选项
  const buildCategoryOptions = (categories, level = 0) => {
    return categories.map(category => {
      const indent = '　'.repeat(level)
      const option = (
        <Option key={category.id} value={category.id}>
          {indent}{category.name}
        </Option>
      )
      
      if (category.children && category.children.length > 0) {
        return [
          option,
          ...buildCategoryOptions(category.children, level + 1)
        ]
      }
      
      return option
    }).flat()
  }
  
  return (
    <div className="book-management">
      <Card className="management-card">
        <div className="management-header">
          <Space>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleAddBook}
            >
              添加书籍
            </Button>
            <Upload
              accept=".txt"
              showUploadList={false}
              beforeUpload={handleUpload}
            >
              <Button icon={<UploadOutlined />}>
                上传书籍
              </Button>
            </Upload>
            <Button 
              icon={<DownloadOutlined />}
              onClick={() => navigate('/admin/pages')}
            >
              生成页面
            </Button>
          </Space>
        </div>
        
        <Divider />
        
        <Table
          dataSource={books}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>
      
      {/* 添加/编辑书籍模态框 */}
      <Modal
        title={editingBook ? "编辑书籍" : "添加书籍"}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setModalVisible(false)
          form.resetFields()
        }}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            paging_mode: 'chapter',
            page_size: 3000
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="title"
                label="书名"
                rules={[{ required: true, message: '请输入书名' }]}
              >
                <Input placeholder="请输入书名" />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="author"
                label="作者"
              >
                <Input placeholder="请输入作者" />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="dynasty"
                label="朝代"
              >
                <Select placeholder="请选择朝代" allowClear>
                  <Option value="春秋">春秋</Option>
                  <Option value="战国">战国</Option>
                  <Option value="秦">秦</Option>
                  <Option value="汉">汉</Option>
                  <Option value="三国">三国</Option>
                  <Option value="晋">晋</Option>
                  <Option value="南北朝">南北朝</Option>
                  <Option value="隋">隋</Option>
                  <Option value="唐">唐</Option>
                  <Option value="五代">五代</Option>
                  <Option value="宋">宋</Option>
                  <Option value="元">元</Option>
                  <Option value="明">明</Option>
                  <Option value="清">清</Option>
                </Select>
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="category_id"
                label="分类"
              >
                <Select 
                  placeholder="请选择分类" 
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {buildCategoryOptions(categories)}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Divider>分页设置</Divider>
          
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="paging_mode"
                label="分页模式"
              >
                <Select>
                  <Option value="chapter">按章节分页</Option>
                  <Option value="fixed">按固定字数分页</Option>
                </Select>
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="page_size"
                label="每页字数"
              >
                <InputNumber 
                  min={100} 
                  max={10000} 
                  placeholder="请输入每页字数"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Divider>动态字段</Divider>
          
          <Row gutter={16}>
            {dynamicFields.map(field => (
              <Col span={12} key={field.id}>
                <Form.Item
                  name={field.field_name}
                  label={field.display_name}
                >
                  {field.field_type === 'number' ? (
                    <InputNumber style={{ width: '100%' }} />
                  ) : field.field_type === 'select' ? (
                    <Select>
                      {field.options?.map(option => (
                        <Option key={option} value={option}>
                          {option}
                        </Option>
                      ))}
                    </Select>
                  ) : (
                    <Input placeholder={`请输入${field.display_name}`} />
                  )}
                </Form.Item>
              </Col>
            ))}
          </Row>
        </Form>
      </Modal>
    </div>
  )
}

export default BookManagement